import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { RegistrationFlow, UpdateRegistrationFlowBody } from '@ory/client';
import { VKratosFormFieldDirective } from 'src/app/core/components/kratos-form/form-slot.directive';
import { KratosFormV2Component } from 'src/app/core/components/kratos-form/form.component';
import { VCardComponent } from 'src/app/core/components/ui-kit/card/card.component';

import { SharedModule } from 'src/app/core/shared.module';
import { AuthRedirectService } from 'src/app/services/authentication/auth-redirect.service';
import {
  ErrAlreadyLoggedIn,
  ErrBrowserLocationChangeRequired,
} from 'src/app/services/authentication/kratos.errors';
import {
  Invitation,
  KratosAuthenticationService,
} from 'src/app/services/authentication/kratos.service';
import { environment } from 'src/environments/environment';
import { VLogoHeaderComponent } from '../components/logo-header/logo-header.component';

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  imports: [
    SharedModule,
    KratosFormV2Component,
    RouterModule,
    VKratosFormFieldDirective,
    VCardComponent,
    VLogoHeaderComponent,
  ],
})
export class RegisterPageComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private auth = inject(KratosAuthenticationService);
  private router = inject(Router);
  private authRedirect = inject(AuthRedirectService);
  private destroyRef = inject(DestroyRef);

  initialFlowLoading = true;
  registrationFlow = signal<RegistrationFlow | undefined>(undefined);
  isCompletingRegistrationFlow = signal(false);

  isLoading = false;

  invitation?: Invitation;
  loadInvitationError?: string;
  isInvite = false;

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      const invitationToken = params['token'];
      const flowId = params['flow'];

      if (flowId) {
        this.loadRegistrationFlow(flowId);
      } else {
        if (invitationToken) {
          this.isInvite = true;
          this.loadInvitation(invitationToken).then(invitation => {
            this.invitation = invitation;
            this.createRegistrationFlow();
          });
        } else {
          this.createRegistrationFlow();
        }
      }
    });
  }

  private loadRegistrationFlow(flowId: string) {
    this.auth.getRegistrationFlow(flowId).then(flow => {
      this.registrationFlow.set(flow);
      this.initialFlowLoading = false;
    });
  }

  private createRegistrationFlow() {
    const returnTo = `${environment.frontendUrl}/login?check=whoami&action=register`;

    this.auth
      .createRegistrationFlow(returnTo)
      .then(flow => {
        // preset email
        if (this.invitation) {
          flow?.ui.nodes.find((n: any) => {
            if (n.attributes.name === 'traits.email') {
              n.attributes.value = this.invitation?.email;
            }
          });
        }

        this.registrationFlow.set(flow);
      })
      .catch(ex => {
        if (ex instanceof ErrAlreadyLoggedIn) {
          this.router.navigate(['/']);
        }

        if (this.isInvite) {
          this.router.navigate(['/organizations/join'], {
            queryParams: { token: this.invitation?.token },
          });
        }
      })
      .finally(() => {
        this.initialFlowLoading = false;
      });
  }

  loadInvitation(token: string): Promise<Invitation> {
    return new Promise((resolve, reject) => {
      return this.auth.getInvitation(token).subscribe({
        next: invitation => {
          resolve(invitation);
        },
        error: error => {
          this.loadInvitationError = error;
          reject(error);
        },
      });
    });
  }

  async submitFlow(data: UpdateRegistrationFlowBody) {
    if (this.isCompletingRegistrationFlow()) {
      return;
    }
    this.isCompletingRegistrationFlow.set(true);

    let isRedirecting = false;
    try {
      if (data.method === 'oidc') {
        // remove traits as they will be provided by the OIDC provider
        delete data.traits;

        await this.auth.completeRegistrationFlowV2(
          this.registrationFlow()!,
          data
        );
      } else if (data.method === 'password') {
        await this.auth.completeRegistrationFlowV2(
          this.registrationFlow()!,
          data
        );
        if (this.isInvite) {
          this.router.navigate(['/organizations/join'], {
            queryParams: { token: this.invitation?.token },
          });
        } else {
          this.router.navigate(['/']);
        }
      }
    } catch (error: any) {
      if (error instanceof ErrBrowserLocationChangeRequired) {
        isRedirecting = true;
        this.authRedirect
          .redirect(error.redirectUri)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => {
            this.isCompletingRegistrationFlow.set(false);
          });
      } else if (data.method === 'password') {
        this.registrationFlow.set(error.response.data);
      }
    } finally {
      // stay busy while the browser leaves for the provider: another
      // provider submit now would invalidate this redirect's OIDC state
      if (!isRedirecting) {
        this.isCompletingRegistrationFlow.set(false);
      }
    }
  }
}
